import './lib/polyfills';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import './index.css';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { App as CapApp } from '@capacitor/app';

if (Capacitor.isNativePlatform()) {
  document.documentElement.classList.add('cap-native');
  void Keyboard.setAccessoryBarVisible({ isVisible: false });

  // Lift the app above the keyboard ourselves, and flag when it's open.
  void Keyboard.addListener('keyboardWillShow', (info) => {
    document.documentElement.style.setProperty('--kb', `${info.keyboardHeight}px`);
    document.documentElement.classList.add('kb-open');
  });
  // Invite links (https://app.opsalo.com/?invite=...) open the app when it's installed (universal links,
  // see public/.well-known/apple-app-site-association). Hand the token to the normal invite screen.
  const openLink = (url: string) => {
    try {
      const token = new URL(url).searchParams.get('invite');
      if (token && new URLSearchParams(window.location.search).get('invite') !== token) window.location.href = `/?invite=${encodeURIComponent(token)}`;
    } catch { /* not a link we handle */ }
  };
  void CapApp.addListener('appUrlOpen', ({ url }) => openLink(url));
  void CapApp.getLaunchUrl().then((r) => { if (r?.url) openLink(r.url); }).catch(() => {});

  void Keyboard.addListener('keyboardWillHide', () => {
    document.documentElement.style.setProperty('--kb', '0px');
    document.documentElement.classList.remove('kb-open');
  });
}

const dsn = import.meta.env.VITE_SENTRY_DSN;
if (dsn) {
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.05,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    beforeSend(event) {
      // Defence-in-depth: never ship message bodies or auth headers.
      if (event.request?.headers) {
        delete event.request.headers.Authorization;
        delete event.request.headers.authorization;
        delete event.request.headers.cookie;
      }
      if (event.request?.data) {
        event.request.data = '<redacted>';
      }
      return event;
    },
  });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<div className="fixed inset-0 bg-black text-white flex items-center justify-center text-xs">Something broke. The team has been notified.</div>}>
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
